import { type ComponentProps, type CSSProperties } from 'react'
import { twMerge } from 'tailwind-merge'
import { HighlightLogoIcon } from './nested/icon'
import { HighlightLogoTitle } from './nested/title'
import { HighlightLogoTile } from './nested/tile'
import { HIGHLIGHT_LOGO_HEIGHTS, type HighlightLogoSize } from './nested/sizes'

/**
 * Fixed colours for a background the theme does not own: a photo, a brand hero. Named by the colour they draw:
 * `dark` and `light` are the on-light and on-dark tokens, `brand` the yellow. With no tone the
 * logo is `currentColor` and follows the theme, which is what almost every placement wants.
 */
const TONE_CLASS = {
  dark: 'text-on-light',
  light: 'text-on-dark',
  brand: 'text-brand-9',
} as const

type HighlightLogoTone = keyof typeof TONE_CLASS

interface HighlightLogoProps extends ComponentProps<'span'> {
  /** A brandbook step, or a frame height in pixels for a slot the ramp does not cover (an icon row). */
  size?: HighlightLogoSize | number
  tone?: HighlightLogoTone
}

/**
 * Highlight's logo. The root owns size and spacing; `HighlightLogo.Icon` and
 * `HighlightLogo.Title` inside it are the symbol and the wordmark. With no children it is the full
 * lockup. The gap is the lockup's own, 7 units of its 29-unit frame, so the two parts composed by
 * hand land exactly where the brand file puts them.
 *
 * Colour is `currentColor`, like a Lucide icon: it inherits, `tone` pins one of the brand's colours,
 * and any `text-*` class overrides both.
 *
 * Anything that shows Highlight's logo uses this, never a copy of the paths and never the word typed
 * in a heading font.
 */
function HighlightLogo({ size = 'sm', tone, className, style, children, ...props }: HighlightLogoProps) {
  // The brandbook sizes the lockup by frame height; `--logo-h` is the one number every part sizes from.
  const height = typeof size === 'number' ? size : HIGHLIGHT_LOGO_HEIGHTS[size]

  return (
    <span
      role="img"
      aria-label="Highlight"
      className={twMerge(
        'inline-flex shrink-0 items-center gap-[calc(var(--logo-h)*7/29)]',
        tone && TONE_CLASS[tone],
        className,
      )}
      style={{ '--logo-h': `${height}px`, ...style } as CSSProperties}
      {...props}
    >
      {children ?? (
        <>
          <HighlightLogoIcon />
          <HighlightLogoTitle />
        </>
      )}
    </span>
  )
}

HighlightLogo.Icon = HighlightLogoIcon
HighlightLogo.Title = HighlightLogoTitle
HighlightLogo.Tile = HighlightLogoTile

export { HighlightLogo, HIGHLIGHT_LOGO_HEIGHTS, type HighlightLogoSize, type HighlightLogoTone }
